import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { usePlanningContext } from '../state/PlanningContext';
import { trackPlanningUpdatesEvent } from '../../../lib/planningUpdatesAnalytics';

const SCALE_MIN = 1;
const SCALE_MAX = 10;
const SCALE_STEPS = SCALE_MAX - SCALE_MIN;
const FILTER_APPLY_IDLE_DELAY_MS = 2000;
const FILTER_APPLY_MAX_DELAY_MS = 5000;
const SCALE_VALUES = Array.from({ length: SCALE_MAX }, (_, index) => index + SCALE_MIN);

const clampScale = (value) => Math.min(SCALE_MAX, Math.max(SCALE_MIN, Math.round(value)));
const isSameRange = (left, right) => Number(left?.min) === Number(right?.min) && Number(left?.max) === Number(right?.max);

// Local lightweight icons to avoid dependency name mismatches
const WindowIcon = ({ size = 14, className, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <rect x="4" y="4" width="16" height="16" rx="2"/>
    <path d="M12 4v16M4 12h16" />
  </svg>
);

const HouseIcon = ({ size = 14, className, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <path d="M3 10.5L12 3l9 7.5"/>
    <path d="M5 10v10h14V10"/>
    <path d="M9 20v-6h6v6"/>
  </svg>
);

const CraneIcon = ({ size = 14, className, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    <path d="M3 20h18"/>
    <path d="M5 20V8h7l3-3h4"/>
    <path d="M15 5v4l3 2"/>
    <circle cx="18" cy="13" r="1.5" />
  </svg>
);

const ScaleRangeSlider = ({ minValue, maxValue, onRangeChange, compact = false }) => {
  const trackRef = useRef(null);
  const draggingRef = useRef(null); // 'min' | 'max' | null
  const pointerIdRef = useRef(null);
  const captureRef = useRef(null);
  const valuesRef = useRef({ min: minValue, max: maxValue });

  useEffect(() => {
    valuesRef.current = { min: minValue, max: maxValue };
  }, [minValue, maxValue]);

  const getValueFromPointer = useCallback((event) => {
    if (!trackRef.current) return null;

    const rect = trackRef.current.getBoundingClientRect();
    if (!rect.width) return null;

    const clientX = event.clientX ?? event.touches?.[0]?.clientX;
    if (clientX == null) return null;

    const pct = (clientX - rect.left) / rect.width;
    return clampScale(SCALE_MIN + pct * SCALE_STEPS);
  }, []);

  const commit = useCallback((thumb, value) => {
    if (value == null) return;

    const current = valuesRef.current;
    if (thumb === 'min') {
      const nextMin = Math.min(value, current.max);
      valuesRef.current = { min: nextMin, max: current.max };
      onRangeChange({ min: nextMin, max: current.max });
      return;
    }

    const nextMax = Math.max(value, current.min);
    valuesRef.current = { min: current.min, max: nextMax };
    onRangeChange({ min: current.min, max: nextMax });
  }, [onRangeChange]);

  const endDrag = useCallback(() => {
    if (pointerIdRef.current != null && captureRef.current) {
      try {
        captureRef.current.releasePointerCapture(pointerIdRef.current);
      } catch (_error) {
        void _error;
      }
    }

    pointerIdRef.current = null;
    captureRef.current = null;
    draggingRef.current = null;
  }, []);

  const beginDrag = useCallback((thumb, event) => {
    if (event.button != null && event.button !== 0) return;

    event.preventDefault();
    event.stopPropagation();
    draggingRef.current = thumb;

    if (event.pointerId != null) {
      pointerIdRef.current = event.pointerId;
      const target = event.currentTarget ?? trackRef.current;
      captureRef.current = target;
      try {
        target.setPointerCapture(event.pointerId);
      } catch (_error) {
        void _error;
      }
    }

    commit(thumb, getValueFromPointer(event));
  }, [commit, getValueFromPointer]);

  const handleTrackPointerDown = useCallback((event) => {
    if (event.button != null && event.button !== 0) return;

    const value = getValueFromPointer(event);
    if (value == null) return;

    const current = valuesRef.current;
    const thumb = Math.abs(value - current.min) <= Math.abs(value - current.max) ? 'min' : 'max';
    beginDrag(thumb, event);
  }, [beginDrag, getValueFromPointer]);

  useEffect(() => {
    const onMove = (event) => {
      if (!draggingRef.current) return;
      event.preventDefault();
      commit(draggingRef.current, getValueFromPointer(event));
    };

    const onUp = () => {
      if (draggingRef.current) endDrag();
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [commit, endDrag, getValueFromPointer]);

  const handleKeyDown = useCallback((thumb) => (event) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'];
    if (!keys.includes(event.key)) return;

    event.preventDefault();
    let nextValue = thumb === 'min' ? valuesRef.current.min : valuesRef.current.max;

    if (event.key === 'Home') nextValue = SCALE_MIN;
    else if (event.key === 'End') nextValue = SCALE_MAX;
    else {
      const step = event.key === 'PageUp' || event.key === 'PageDown' ? 2 : 1;
      const direction = event.key === 'ArrowRight' || event.key === 'PageUp' ? 1 : -1;
      nextValue = clampScale(nextValue + (step * direction));
    }

    commit(thumb, nextValue);
  }, [commit]);

  const minPct = ((minValue - SCALE_MIN) / SCALE_STEPS) * 100;
  const maxPct = ((maxValue - SCALE_MIN) / SCALE_STEPS) * 100;

  return (
    <div className={compact ? 'space-y-2' : 'space-y-2.5'}>
      <div
        ref={trackRef}
        className="relative h-9 cursor-pointer border border-[var(--c-border)] bg-[var(--c-bg)] px-3 touch-none"
        onPointerDown={handleTrackPointerDown}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
      >
        <div className="absolute inset-x-3 top-1/2 h-px -translate-y-1/2 bg-[var(--c-border)]" />
        <div
          className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-[var(--c-accent)]"
          style={{
            left: `${minPct}%`,
            width: `${Math.max(maxPct - minPct, 0)}%`
          }}
        />

        {SCALE_VALUES.map((value) => {
          const pct = ((value - SCALE_MIN) / SCALE_STEPS) * 100;
          const isSelected = value >= minValue && value <= maxValue;
          return (
            <span
              key={value}
              className={`pointer-events-none absolute top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full ${
                isSelected ? 'bg-[var(--c-accent)]' : 'bg-[var(--c-text-secondary)]/35'
              }`}
              style={{ left: `${pct}%` }}
              aria-hidden
            />
          );
        })}

        <button
          type="button"
          className="absolute top-1/2 z-10 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 border-2 border-[var(--c-text-primary)] bg-[var(--c-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)]/45 cursor-grab active:cursor-grabbing touch-none"
          style={{ left: `${minPct}%` }}
          onPointerDown={(event) => beginDrag('min', event)}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={handleKeyDown('min')}
          role="slider"
          aria-label="Minimum impact"
          aria-valuemin={SCALE_MIN}
          aria-valuemax={SCALE_MAX}
          aria-valuenow={minValue}
          aria-valuetext={`Minimum impact ${minValue}`}
        />

        <button
          type="button"
          className="absolute top-1/2 z-10 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 border-2 border-[var(--c-text-primary)] bg-[var(--c-surface)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--c-accent)]/45 cursor-grab active:cursor-grabbing touch-none"
          style={{ left: `${maxPct}%` }}
          onPointerDown={(event) => beginDrag('max', event)}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={handleKeyDown('max')}
          role="slider"
          aria-label="Maximum impact"
          aria-valuemin={SCALE_MIN}
          aria-valuemax={SCALE_MAX}
          aria-valuenow={maxValue}
          aria-valuetext={`Maximum impact ${maxValue}`}
        />
      </div>

      <div className="flex justify-between px-1 text-[10px] font-medium text-[var(--c-text-secondary)]">
        <span>1</span>
        <span>3</span>
        <span>6</span>
        <span>10</span>
      </div>
    </div>
  );
};

const ScaleRatingFilter = ({ compact = false, forceShowAdvanced = false }) => {
  void forceShowAdvanced;

  const {
    selectedScaleCategory,
    setSelectedScaleCategory,
    hasAnimated,
    scaleRange,
    setScaleRange
  } = usePlanningContext();

  const [activeCategory, setActiveCategory] = useState(selectedScaleCategory || 'all');
  const [localRange, setLocalRange] = useState(scaleRange);
  const [isApplyPending, setIsApplyPending] = useState(false);
  const applyDebounceTimerRef = useRef(null);
  const applyMaxTimerRef = useRef(null);
  const pendingRangeRef = useRef(scaleRange);
  const pendingCategoryRef = useRef(selectedScaleCategory || 'all');
  const appliedRangeRef = useRef(scaleRange);
  const appliedCategoryRef = useRef(selectedScaleCategory || 'all');
  const isMountedRef = useRef(true);
  const lastRangeAnalyticsRef = useRef(`${scaleRange?.min ?? 1}-${scaleRange?.max ?? 10}`);

  const setApplyPendingSafe = useCallback((nextValue) => {
    if (!isMountedRef.current) return;
    setIsApplyPending(nextValue);
  }, []);

  useEffect(() => {
    setActiveCategory(selectedScaleCategory || 'all');
    appliedCategoryRef.current = selectedScaleCategory || 'all';
    setApplyPendingSafe(false);
  }, [selectedScaleCategory, setApplyPendingSafe]);

  useEffect(() => {
    setLocalRange(scaleRange);
    pendingRangeRef.current = scaleRange;
    appliedRangeRef.current = scaleRange;
    setApplyPendingSafe(false);
  }, [scaleRange, setApplyPendingSafe]);

  useEffect(() => {
    pendingCategoryRef.current = selectedScaleCategory || 'all';
  }, [selectedScaleCategory]);

  const scaleOptions = useMemo(() => [
    { id: 'low', label: 'Low', rangeLabel: '1-3', icon: WindowIcon, min: 1, max: 3 },
    { id: 'medium', label: 'Med', rangeLabel: '4-6', icon: HouseIcon, min: 4, max: 6 },
    { id: 'high', label: 'High', rangeLabel: '7-10', icon: CraneIcon, min: 7, max: 10 },
    { id: 'all', label: 'All', rangeLabel: '1-10', icon: WindowIcon, min: 1, max: 10 }
  ], []);

  const getCategoryFromRange = useCallback((range) => {
    const matchingPreset = scaleOptions.find((option) => (
      option.min === range.min && option.max === range.max
    ));
    return matchingPreset ? matchingPreset.id : 'all';
  }, [scaleOptions]);

  const clearApplyTimers = useCallback(() => {
    if (applyDebounceTimerRef.current) {
      window.clearTimeout(applyDebounceTimerRef.current);
      applyDebounceTimerRef.current = null;
    }
    if (applyMaxTimerRef.current) {
      window.clearTimeout(applyMaxTimerRef.current);
      applyMaxTimerRef.current = null;
    }
  }, []);

  const applyPendingFiltersNow = useCallback(() => {
    clearApplyTimers();
    const pendingRange = pendingRangeRef.current;
    const pendingCategory = pendingCategoryRef.current || 'all';

    if (!isSameRange(appliedRangeRef.current, pendingRange)) {
      setScaleRange(pendingRange);
      appliedRangeRef.current = pendingRange;
    }

    if ((appliedCategoryRef.current || 'all') !== pendingCategory) {
      setSelectedScaleCategory(pendingCategory);
      appliedCategoryRef.current = pendingCategory;
    }
    setApplyPendingSafe(false);
  }, [clearApplyTimers, setApplyPendingSafe, setScaleRange, setSelectedScaleCategory]);

  const applyFiltersNow = useCallback((nextRange, nextCategory) => {
    const normalizedNextCategory = nextCategory || 'all';

    clearApplyTimers();
    pendingRangeRef.current = nextRange;
    pendingCategoryRef.current = normalizedNextCategory;

    if (!isSameRange(appliedRangeRef.current, nextRange)) {
      setScaleRange(nextRange);
      appliedRangeRef.current = nextRange;
    }

    if ((appliedCategoryRef.current || 'all') !== normalizedNextCategory) {
      setSelectedScaleCategory(normalizedNextCategory);
      appliedCategoryRef.current = normalizedNextCategory;
    }

    setApplyPendingSafe(false);
  }, [clearApplyTimers, setApplyPendingSafe, setScaleRange, setSelectedScaleCategory]);

  const scheduleDelayedApply = useCallback((nextRange, nextCategory) => {
    const normalizedNextCategory = nextCategory || 'all';
    const hasPendingChange = (
      !isSameRange(appliedRangeRef.current, nextRange)
      || (appliedCategoryRef.current || 'all') !== normalizedNextCategory
    );

    if (!hasPendingChange) {
      clearApplyTimers();
      setApplyPendingSafe(false);
      return;
    }

    pendingRangeRef.current = nextRange;
    pendingCategoryRef.current = normalizedNextCategory;
    setApplyPendingSafe(true);

    if (applyDebounceTimerRef.current) {
      window.clearTimeout(applyDebounceTimerRef.current);
    }

    applyDebounceTimerRef.current = window.setTimeout(() => {
      applyPendingFiltersNow();
    }, FILTER_APPLY_IDLE_DELAY_MS);

    if (!applyMaxTimerRef.current) {
      applyMaxTimerRef.current = window.setTimeout(() => {
        applyPendingFiltersNow();
      }, FILTER_APPLY_MAX_DELAY_MS);
    }
  }, [applyPendingFiltersNow, clearApplyTimers, setApplyPendingSafe]);

  useEffect(() => () => {
    applyPendingFiltersNow();
  }, [applyPendingFiltersNow]);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const handleCategoryChange = useCallback((categoryId) => {
    setActiveCategory(categoryId);

    const preset = scaleOptions.find((option) => option.id === categoryId);
    if (!preset) return;

    const nextRange = { min: preset.min, max: preset.max };
    trackPlanningUpdatesEvent('planning_updates_impact_preset_selected', {
      placement: compact ? 'impact_scale_compact' : 'impact_scale',
      status: categoryId,
      value: nextRange.min,
      metadata: {
        impact_preset: categoryId,
        impact_min: nextRange.min,
        impact_max: nextRange.max,
        previous_impact_min: scaleRange?.min ?? 1,
        previous_impact_max: scaleRange?.max ?? 10
      }
    });
    setLocalRange(nextRange);
    applyFiltersNow(nextRange, categoryId);
  }, [applyFiltersNow, compact, scaleOptions, scaleRange]);

  const handleRangeChange = useCallback((range) => {
    const nextCategory = getCategoryFromRange(range);
    const signature = `${range.min}-${range.max}`;
    if (signature !== lastRangeAnalyticsRef.current) {
      lastRangeAnalyticsRef.current = signature;
      trackPlanningUpdatesEvent('planning_updates_impact_range_changed', {
        placement: compact ? 'impact_scale_compact' : 'impact_scale',
        status: nextCategory,
        value: range.min,
        metadata: {
          impact_min: range.min,
          impact_max: range.max,
          derived_impact_preset: nextCategory
        }
      });
    }
    setLocalRange(range);
    setActiveCategory(nextCategory);
    scheduleDelayedApply(range, nextCategory);
  }, [compact, getCategoryFromRange, scheduleDelayedApply]);

  return (
    <motion.div
      className="space-y-4"
      initial={hasAnimated.filters ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="relative z-10 mt-1 w-full">
        <div className="mb-3 flex min-h-[24px] items-center justify-between gap-3">
          <div className="pr-data">
            Range <span className="font-['Newsreader',Georgia,serif] text-lg text-[var(--c-text-primary)]">{localRange.min}&ndash;{localRange.max}</span>
          </div>

          {isApplyPending && (
            <span className="pr-data text-[var(--c-accent)]" role="status" aria-live="polite">
              Applying...
            </span>
          )}
        </div>

        <ScaleRangeSlider
          minValue={localRange.min}
          maxValue={localRange.max}
          onRangeChange={handleRangeChange}
          compact={compact}
          />

        <div className="mt-3">
          <div className="pr-segment w-full">
            {scaleOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = activeCategory === option.id || (
                option.id === 'all' && localRange.min === 1 && localRange.max === 10
              );

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => handleCategoryChange(option.id)}
                  title={`${option.label} (${option.min}-${option.max})`}
                  aria-pressed={isSelected}
                  className="pr-segment-item flex-1 justify-center"
                >
                  <Icon size={14} />
                  <span>{option.label}</span>
                  <span className="pr-segment-count">{option.rangeLabel}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ScaleRatingFilter;
